import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import {
  bilingual,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  sectionSubtitle,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A 1950s diner: checkerboard strips, a glowing script sign between two round photos, and the
 * menu on cards with pill-shaped headers.
 */

const S = '.mb-t-retro-diner';

function card(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="rd-pill">${sectionTitle(ctx, b.section, 'rd-title')}</div>${b.continued ? '' : sectionSubtitle(ctx, b.section, 'rd-sub')}`
    : '';
  return `<div class="rd-card${head ? ' rd-has-head' : ''}">${head}${b.items.map((i) => listItem(ctx, i, 'rd')).join('')}</div>`;
}

export const retroDiner: MenuTemplate = {
  id: 'retro-diner',
  name: 'Retro diner',
  description: 'Checkerboard strips, a glowing script sign and menu cards with pill headers, like a 50s diner. Made for burgers.',
  defaultTheme: {
    backgroundColor: '#fbefd9',
    surfaceColor: '#fffaf0',
    textColor: '#2a2522',
    primaryColor: '#1b998b',
    accentColor: '#e63946',
    fontId: 'street',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'cherry', name: 'Cherry', theme: {} },
    { id: 'mint', name: 'Mint', theme: { backgroundColor: '#c9eee3', surfaceColor: '#f3fffb', primaryColor: '#ef476f', accentColor: '#26547c' } },
    { id: 'neon', name: 'Neon night', theme: { backgroundColor: '#12142b', surfaceColor: '#1b1e3d', textColor: '#f4f4ff', primaryColor: '#00f5d4', accentColor: '#ff4ecd' } },
    { id: 'butter', name: 'Butter', theme: { backgroundColor: '#fff1a8', surfaceColor: '#fffbe2', primaryColor: '#335c67', accentColor: '#d1495b' } },
  ],
  cuisines: ['burgers'],
  uses: { featured: false, sectionImages: false, brand: true, decorations: ['Round photo left of the sign', 'Round photo right of the sign'] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(
      groupBySection(board),
      listColumns(board, {
        count: portrait ? 2 : 3,
        capacity: portrait ? 92 : 46,
        heading: 5,
        item: { nameEm: 1.3, descEm: 0.82, perLine: portrait ? 30 : 34, gap: 1.2 },
        extra: () => 3,
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const left = image(ctx, board.decorMediaIds[0], 'rd-round');
    const right = image(ctx, board.decorMediaIds[1], 'rd-round');
    const sign = [
      '<div class="rd-sign">',
      bilingual(ctx, board.brand.name ?? board.settings.title ?? board.name, board.brand.nameAr ?? board.settings.titleAr, 'rd-brand'),
      bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'rd-tagline'),
      '</div>',
    ].join('');
    const cols = `<div class="rd-cols">${ctx.page.columns
      .map((c) => `<div class="rd-col">${c.map((b) => card(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
    return `<div class="rd-checker"></div><div class="rd-body"><div class="rd-header">${left}${sign}${right}</div>${cols}</div><div class="rd-checker"></div>`;
  },

  css: `
${S}{padding:0}
${S} .rd-checker{flex:0 0 2.2em;background:repeating-conic-gradient(var(--mb-text) 0 25%,var(--mb-surface) 0 50%) 0 0/2.2em 2.2em;opacity:.9}
${S}.mb-v-neon .rd-checker{background:repeating-conic-gradient(var(--mb-accent) 0 25%,var(--mb-bg) 0 50%) 0 0/2.2em 2.2em}
${S} .rd-body{flex:1;min-height:0;display:flex;flex-direction:column;padding:1.8em 3em 1.6em}
${S} .rd-header{display:flex;align-items:center;justify-content:center;gap:3em;margin-bottom:2em}
${S} .rd-round{width:9.5em;height:9.5em;border-radius:50%;object-fit:cover;border:.45em solid var(--mb-primary);
  box-shadow:0 0 0 .3em var(--mb-surface),0 .8em 1.6em rgba(0,0,0,.2)}
${S} .rd-sign{text-align:center}
${S} .rd-brand{font-family:var(--mb-font-script);font-size:5em;line-height:1.05;color:var(--mb-accent);
  text-shadow:.04em .04em 0 var(--mb-primary),0 0 .35em color-mix(in srgb,var(--mb-accent) 45%,transparent)}
${S}.mb-v-neon .rd-brand{color:#fff;text-shadow:0 0 .08em #fff,0 0 .25em var(--mb-accent),0 0 .6em var(--mb-accent)}
${S} .rd-brand.mb-2nd{font-size:2.6em}
${S} .rd-tagline{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.4em;letter-spacing:.3em;
  text-transform:uppercase;color:var(--mb-primary)}
${S} .rd-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:2.4em}
${S}.mb-portrait .rd-cols{grid-template-columns:1fr 1fr}
${S} .rd-col{min-width:0;display:flex;flex-direction:column;gap:2.6em}
${S} .rd-card{position:relative;background:var(--mb-surface);border:.22em solid var(--mb-primary);border-radius:1.2em;
  padding:1.4em 1.5em .6em;box-shadow:.45em .45em 0 var(--mb-primary)}
${S} .rd-has-head{padding-top:2.4em}
${S} .rd-pill{position:absolute;top:-1.3em;left:50%;transform:translateX(-50%);background:var(--mb-accent);color:var(--mb-surface);
  padding:.25em 1.4em;border-radius:99em;white-space:nowrap;box-shadow:0 .3em 0 var(--mb-primary)}
${S} .rd-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;text-transform:uppercase;line-height:1;letter-spacing:.04em}
${S} .rd-title.mb-2nd{font-size:1.3em;text-align:center}
${S} .rd-sub{text-align:center;font-family:var(--mb-font-script);color:var(--mb-primary);margin:-.6em 0 .6em}
${listCss(S, 'rd', { name: '1.3em', price: '1.4em', desc: '.82em', leader: '.15em dotted color-mix(in srgb,var(--mb-text) 35%,transparent)' })}
${S} .rd-item{margin-bottom:.9em}
${S} .rd-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;letter-spacing:.03em}
${S} .rd-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-accent)}
`,
};
